import { GridColDef } from '@mui/x-data-grid-pro';
import { Dayjs } from 'dayjs';
import { useEffect, useMemo, useRef, type JSX } from 'react';

import { WelcomeBonusReport, WelcomeBonusReportTotals } from '../../../@types/api';
import API_ROUTES from '../../../constants/apiRoutes';
import { appendTotalsRow, getTotalsRowClass } from '../../../helpers';
import { createNumberColumn, createPercentageColumn } from '../../../helpers/columns';
import { usePagination } from '../../../hooks/usePagination';
import useSort from '../../../hooks/useSort';
import { createColumn, formatSnakeCaseString } from '../../../utils';
import TableTemplate from '../../templates/TableTemplate';

type WelcomeBonusReportsPageProps = {
  date: { fromTimestamp: Dayjs; toTimestamp: Dayjs };
  headerContent: JSX.Element;
};

const getMobileCardHeaderItems = (report: WelcomeBonusReport) => [
  { label: 'Product', value: formatSnakeCaseString(report.product), flex: 1 },
  { label: 'Opt-ins', value: report.totalBonusOptIns, flex: 1 },
  { label: 'True Cost', value: `${Number(report.bonusTrueCostPercent).toFixed(2)}%`, flex: 1 },
];

const generateTotalsRow = (totals?: WelcomeBonusReportTotals) => {
  if (totals) {
    return {
      ...totals,
      id: 'totals',
      bonusType: 'totals',
      product: 'totals',
      isTotalsRow: true,
    };
  }
};

const WelcomeBonusReportsPage = ({ date, headerContent }: WelcomeBonusReportsPageProps) => {
  const fromTimestamp = date.fromTimestamp.valueOf();
  const toTimestamp = date.toTimestamp.valueOf();
  const hasMounted = useRef(false);
  const { data, updateQueryParams, isFetching, isLoading, changeQuery } = usePagination<WelcomeBonusReport>(
    API_ROUTES.REPORTS.WELCOME_BONUS,
    {
      page: 1,
      limit: 25,
      fromTimestamp,
      toTimestamp,
    },
    undefined,
    'crm'
  );
  const { handleSort } = useSort(changeQuery);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    changeQuery({
      fromTimestamp,
      toTimestamp,
    });
  }, [changeQuery, fromTimestamp, toTimestamp]);

  const welcomeBonusReportItems = data?.items.map((item) => ({ ...item, id: `welcome-bonus-report-${item.product}` }));
  const welcomeBonusTotalsRow = generateTotalsRow(data?.totals as WelcomeBonusReportTotals | undefined);
  const aggregatedData = appendTotalsRow(welcomeBonusReportItems, welcomeBonusTotalsRow);

  const columns: GridColDef[] = useMemo(
    () => [
      createColumn('bonusType', 'Bonus Type', {
        valueFormatter: (value, row, column, apiRef) => formatSnakeCaseString(value),
      }),
      createColumn('product', 'Product', {
        valueFormatter: (value, row, column, apiRef) => formatSnakeCaseString(value),
      }),
      createNumberColumn('totalFtds', 'Total FTDs in Period'),
      createNumberColumn('totalBonusOptIns', 'Total Bonus Opt-Ins'),
      createPercentageColumn('percentageSplit', 'Percentage Split (%)'),
      createPercentageColumn('bonusOptInRate', 'Bonus Opt-In Rate'),
      createNumberColumn('averageDepositAmount', 'Average Deposit Amount'),
      createNumberColumn('activeBonuses', 'Active Bonuses'),
      createNumberColumn('completed', 'Completed'),
      createNumberColumn('failed', 'Expired / Depleted'),
      createNumberColumn('forfeited', 'Forfeited'),
      createNumberColumn('totalFtdVolume', 'Total FTD Volume'),
      createNumberColumn('totalBonusLiability', 'Total Bonus Liability'),
      createNumberColumn('totalCashReleased', 'Total Cash Released'),
      createPercentageColumn('bonusTrueCostPercent', 'Bonus True Cost'),
      createPercentageColumn('houseKeepPercent', 'House Keep'),
    ],
    []
  );

  return (
    <TableTemplate
      rows={aggregatedData}
      columns={columns}
      loading={isFetching || isLoading}
      rowCount={data?.count ?? 1}
      handlePaginationModelChange={updateQueryParams}
      handleSort={handleSort}
      getRowClassName={getTotalsRowClass}
      changeQuery={changeQuery}
      filterType='welcome-bonus-reports'
      getRowId={(row) => row.id}
      mobileCardHeaderItems={getMobileCardHeaderItems as never}
      customTableHeaderContent={headerContent}
      showExport
    />
  );
};

export default WelcomeBonusReportsPage;
